import type { FC } from 'react';
import { useCallback } from 'react';

import { useHistory } from 'react-router-dom';

import {
  getCategoryFromSource,
  getCategoryIcon,
  getNotificationLink,
  getRenderedContent,
} from 'features/notifications/lib/helpers';
import type { FeedItem, useNotificationFeed } from 'features/notifications/lib/NotificationFeedContext';

import { NotificationItem } from './NotificationItem';

type FeedClient = ReturnType<typeof useNotificationFeed>['feedClient'];

type NotificationSnackbarContentProps = {
  item: FeedItem;
  feedClient: FeedClient;
  onClose: () => void;
};

export const NotificationSnackbarContent: FC<NotificationSnackbarContentProps> = ({ item, feedClient, onClose }) => {
  const history = useHistory();
  const category = getCategoryFromSource(item.source);
  const renderedContent = getRenderedContent(item.blocks);
  const link = getNotificationLink(category, item);
  const icon = getCategoryIcon(category);
  const isUnread = !item.read_at;

  const handleClick = useCallback(() => {
    if (!link) {
      return;
    }
    feedClient.markAsRead(item);
    onClose();
    history.push(link);
  }, [feedClient, item, link, onClose, history]);

  const handleMarkAsRead = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      feedClient.markAsRead(item);
      onClose();
    },
    [feedClient, item, onClose],
  );

  return (
    <NotificationItem
      item={item}
      icon={icon}
      renderedContent={renderedContent}
      isUnread={isUnread}
      category={category}
      notificationType="popup"
      onClick={link ? handleClick : undefined}
      onClose={onClose}
      onMarkAsRead={handleMarkAsRead}
      showDotIndicator={false}
      className="shadow-lg w-[404px] top-[72px]"
    />
  );
};

NotificationSnackbarContent.displayName = 'NotificationSnackbarContent';
